import React from 'react';
import memoizeOne, { type MemoizedFn } from 'memoize-one';
import type { Locale } from '@jira/platform__supported-locales/src/supported-locales.tsx';
import BoardIcon from '@atlaskit/icon/core/board';
import DashboardIcon from '@atlaskit/icon/core/dashboard';
import FilterIcon from '@atlaskit/icon/core/filter';
import PagesIcon from '@atlaskit/icon/core/pages';
import RoadmapIcon from '@atlaskit/icon/core/roadmap';
import { ContainerAvatar } from '@atlaskit/navigation-system/side-nav-items/container-avatar';
import { softwarePlanTimelineRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanTimelineRoute.tsx';
import { generatePath } from '@atlassian/react-resource-router';
import {
	BOARD,
	DASHBOARD,
	FILTER,
	PLAN,
	PROJECT,
	QUEUE,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-recent/src/common/constants.tsx';

import type { ObjectListFlyoutHookResults } from '@atlassian/navigation-common/types';
import type { IntlShape } from '@atlassian/jira-intl';
import type { useStarredItemsPaged$data } from '../../../controllers/use-starred-items/__generated__/useStarredItemsPaged.graphql';
import type { JiraStarredOLFItem } from '../../types.tsx';

type NonNullableEdges = NonNullable<useStarredItemsPaged$data['jira']['starredItems']>['edges'];
type Edge = NonNullable<NonNullableEdges>[number];
export type GQLJiraItem = NonNullable<NonNullable<Edge>['node']>;

export const getSections: MemoizedFn<
	(items: GQLJiraItem[], deps: Deps) => ObjectListFlyoutHookResults<JiraStarredOLFItem>['sections']
> = memoizeOne(getSectionItems);

function getSectionItems(
	items: GQLJiraItem[],
): ObjectListFlyoutHookResults<JiraStarredOLFItem>['sections'] {
	return [{ items: items.map((i) => toOLFItem(i)).filter(Boolean) }];
}

function toOLFItem(item: GQLJiraItem): JiraStarredOLFItem | undefined {
	switch (item.__typename) {
		case 'JiraBoard': {
			const isLocationless = item.boardUrl?.includes('locationless=true');

			return isLocationless
				? undefined
				: {
						analytics: { itemId: String(item?.boardId), itemType: BOARD },
						elemBefore: <BoardIcon spacing="spacious" color="currentColor" label="" />,
						href: item.boardUrl ?? '',
						id: item.id,
						primaryLabel: String(item.boardTitle ?? ''),
						typename: item.__typename,
					};
		}

		case 'JiraDashboard':
			return {
				analytics: { itemId: String(item?.dashboardId), itemType: DASHBOARD },
				elemBefore: <DashboardIcon spacing="spacious" color="currentColor" label="" />,
				href: item.dashboardUrl ?? '',
				id: item.id,
				primaryLabel: String(item.dashboardTitle ?? ''),
				typename: item.__typename,
			};

		case 'JiraPlan': {
			const planId = item.planId;
			const scenarioId = item.scenario?.scenarioId ?? '';
			const planUrl = generatePath(softwarePlanTimelineRoute.path, { planId, scenarioId });

			return {
				analytics: { itemId: String(item?.planId), itemType: PLAN },
				elemBefore: <RoadmapIcon spacing="spacious" color="currentColor" label="" />,
				href: planUrl,
				id: item.id,
				primaryLabel: String(item.planTitle ?? ''),
				typename: item.__typename,
			};
		}

		case 'JiraProject':
			return {
				analytics: { itemId: String(item?.projectId), itemType: PROJECT },
				elemBefore: <ContainerAvatar src={item.avatar?.large ?? ''} />,
				href: item.defaultNavigationItem?.url ?? `/browse/${item.key}`,
				id: item.id,
				primaryLabel: item.projectTitle,
				typename: item.__typename,
			};

		case 'JiraSystemFilter':
		case 'JiraCustomFilter':
			return {
				analytics: { itemId: String(item?.filterId), itemType: FILTER },
				elemBefore: <FilterIcon spacing="spacious" color="currentColor" label="" />,
				href: item.filterUrl ?? '',
				id: item.id,
				primaryLabel: item.filterTitle,
				typename: item.__typename,
			};

		case 'JiraServiceManagementQueue':
			return {
				analytics: { itemId: String(item?.queueId), itemType: QUEUE },
				elemBefore: <PagesIcon spacing="spacious" color="currentColor" label="" />,
				href: item.queueUrl ?? '',
				id: item.id,
				primaryLabel: String(item.queueTitle ?? ''),
				typename: item.__typename,
			};
		default:
			return undefined;
	}
}

type Deps = {
	formatMessage: IntlShape['formatMessage'];
	locale: Locale;
};
